import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import CourseForm from './CourseForm';
import MaterialsManager from './MaterialsManager';
import AssessmentsManager from './AssessmentsManager';
import ConfirmModal from '../shared/ConfirmModal';
import { formatCourseSchedule } from '../../utils/courseSchedule';

const TYPE_LABELS = {
  long_course: 'Long Program',
  block: 'Short Program',
  workshop: 'Workshop'
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
  archived: 'bg-yellow-100 text-yellow-700'
};

const LOCATION_LABELS = {
  online: 'Online',
  in_person: 'In-Person',
  hybrid: 'Hybrid'
};

const CoursesPage = ({ creating }) => {
  const { studio } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(creating || false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingStep, setEditingStep] = useState(1);
  const [managingMaterials, setManagingMaterials] = useState(null);
  const [managingAssessments, setManagingAssessments] = useState(null);
  const [error, setError] = useState('');
  const [confirmModal, setConfirmModal] = useState({ open: false, courseId: null });
  const [tab, setTab] = useState('active'); // 'active' | 'archived'
  const [copiedId, setCopiedId] = useState(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [syncingCourseId, setSyncingCourseId] = useState(null);
  const [invitingCourseId, setInvitingCourseId] = useState(null);
  const [syncingMaterialsCourseId, setSyncingMaterialsCourseId] = useState(null);
  const [actionMessage, setActionMessage] = useState('');

  // Re-clicking "Offerings" in the sidebar while inside the Materials or
  // Assessments sub-view (or an edit form) should drop you back to the list.
  // Those sub-views are local state, so a same-route navigation wouldn't
  // otherwise reset them — watch the location key and clear on any new nav.
  const location = useLocation();
  const lastNavKeyRef = useRef(location.key);
  useEffect(() => {
    if (location.key === lastNavKeyRef.current) return;
    lastNavKeyRef.current = location.key;
    setManagingMaterials(null);
    setManagingAssessments(null);
    setEditingCourse(null);
    setEditingStep(1);
  }, [location.key]);

  const fetchCourses = async () => {
    try {
      const { data } = await api.get('/courses');
      setCourses(data.courses);
    } catch (err) {
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchGoogleStatus = async () => {
    try {
      const { data } = await api.get('/google/classroom/status');
      setGoogleConnected(data.connected);
    } catch (err) {
      // Google not configured or not connected — that's fine
    }
  };

  useEffect(() => { fetchCourses(); fetchGoogleStatus(); }, []);

  const handleSaved = (course) => {
    setShowForm(false);
    setEditingCourse(null);
    fetchCourses();
  };

  const handleStatusChange = async (courseId, newStatus) => {
    try {
      await api.patch(`/courses/${courseId}/status`, { status: newStatus });
      fetchCourses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleArchive = async (courseId) => {
    try {
      await api.delete(`/courses/${courseId}`);
      setConfirmModal({ open: false, courseId: null });
      fetchCourses();
    } catch (err) {
      setConfirmModal({ open: false, courseId: null });
      setError(err.response?.data?.error || 'Failed to archive course');
    }
  };

  const handleSyncToClassroom = async (courseId) => {
    setSyncingCourseId(courseId);
    try {
      const { data } = await api.post(`/google/classroom/create-course/${courseId}`);
      setError('');
      fetchCourses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sync to Google Classroom');
    } finally {
      setSyncingCourseId(null);
    }
  };

  const handleInviteStudents = async (courseId) => {
    setInvitingCourseId(courseId);
    setActionMessage('');
    try {
      const { data } = await api.post(`/google/classroom/invite-students/${courseId}`);
      setActionMessage(`Invited ${data.invited || 0} student(s) to Classroom`);
      setTimeout(() => setActionMessage(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to invite students');
    } finally {
      setInvitingCourseId(null);
    }
  };

  const handleSyncMaterials = async (courseId) => {
    setSyncingMaterialsCourseId(courseId);
    setActionMessage('');
    try {
      const { data } = await api.post(`/google/classroom/sync-materials/${courseId}`);
      setActionMessage(`Synced ${data.synced || 0} week(s) of materials to Classroom`);
      setTimeout(() => setActionMessage(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sync materials');
    } finally {
      setSyncingMaterialsCourseId(null);
    }
  };

  if (managingMaterials) {
    return (
      <MaterialsManager
        courseId={managingMaterials._id}
        courseName={managingMaterials.name}
        onBack={() => setManagingMaterials(null)}
      />
    );
  }

  if (managingAssessments) {
    return (
      <AssessmentsManager
        courseId={managingAssessments._id}
        courseName={managingAssessments.name}
        onBack={() => { setManagingAssessments(null); fetchCourses(); }}
      />
    );
  }

  if (showForm || editingCourse) {
    return (
      <CourseForm
        studio={studio}
        course={editingCourse}
        initialStep={editingStep}
        onSaved={handleSaved}
        onCancel={() => { setShowForm(false); setEditingCourse(null); setEditingStep(1); }}
      />
    );
  }

  if (loading) {
    return <div className="text-gray-500">Loading courses...</div>;
  }

  const stripeConnected = studio?.stripeConnectStatus === 'active';
  const activeCourses = courses.filter(c => c.status !== 'archived');
  const archivedCourses = courses.filter(c => c.status === 'archived');
  const displayedCourses = tab === 'active' ? activeCourses : archivedCourses;

  const formatSchedule = (course) => {
    if (course.type === 'workshop' && course.schedule?.sessionDate) {
      return new Date(course.schedule.sessionDate).toLocaleDateString();
    }
    return formatCourseSchedule(course, { short: true, includeEndTime: false }) || null;
  };

  const formatPrice = (course) => {
    if (!course.billingPlans?.length) return null;
    return course.billingPlans.map(plan => {
      const freq = plan.frequency === 'one_time' ? '' : `/${plan.frequency.replace('_', ' ')}`;
      return `$${plan.amount}${freq}`;
    }).join('  ·  ');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Offerings</h1>
        {stripeConnected && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
          >
            + New Offering
          </button>
        )}
      </div>

      {/* Stripe Connect Required Banner */}
      {!stripeConnected && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <svg className="w-10 h-10 text-amber-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
          </svg>
          <h3 className="text-lg font-semibold text-amber-800 mb-2">Connect Stripe to create offerings</h3>
          <p className="text-sm text-amber-700 mb-4 max-w-md mx-auto">
            You need to connect your Stripe account before you can create courses and accept payments from students.
          </p>
          <Link
            to="/dashboard/settings?tab=integrations"
            className="inline-flex items-center gap-2 bg-amber-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
          >
            Go to Settings &rarr; Integrations
          </Link>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        <button
          onClick={() => setTab('active')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'active'
              ? 'border-brand-500 text-brand-500'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Active ({activeCourses.length})
        </button>
        <button
          onClick={() => setTab('archived')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'archived'
              ? 'border-brand-500 text-brand-500'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Archived ({archivedCourses.length})
        </button>
      </div>

      {actionMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {actionMessage}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {displayedCourses.length === 0 ? (
        <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm text-center">
          {tab === 'active' ? (
            stripeConnected ? (
              <>
                <div className="w-12 h-12 bg-brand-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" /></svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Create your first class offering</h3>
                <p className="text-gray-500 mb-1">Set up the schedule, pricing, and start enrolling students.</p>
                <p className="text-xs text-gray-400 mb-4">You can create ongoing classes, fixed-length blocks, or one-time workshops.</p>
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-block bg-brand-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-brand-600 transition-colors"
                >
                  Create Offering
                </button>
              </>
            ) : (
              <>
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" /></svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Connect Stripe first</h3>
                <p className="text-gray-500 mb-4">You need to connect your Stripe account before creating paid offerings.</p>
                <Link to="/dashboard/settings?tab=integrations" className="inline-block bg-brand-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-brand-600 transition-colors">
                  Connect Stripe
                </Link>
              </>
            )
          ) : (
            <>
              <h3 className="text-lg font-semibold mb-2">No archived courses</h3>
              <p className="text-gray-500">Archived courses will appear here.</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {displayedCourses.map(course => (
            <div key={course._id} className={`bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden ${tab === 'archived' ? 'opacity-60' : ''}`}>
              <div className="p-5">
                {/* Header */}
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-2 h-2 rounded-full ${course.status === 'active' ? 'bg-green-500' : course.status === 'draft' ? 'bg-gray-400' : 'bg-yellow-500'}`} />
                  <h3 className="font-semibold text-gray-900">{course.name}</h3>
                </div>

                {/* Subtitle */}
                <p className="text-sm text-gray-500 ml-4 mb-3">
                  {course.danceStyle || ''}
                  {course.danceStyle && course.levelName ? ' · ' : ''}
                  {course.levelName || ''}
                </p>

                {/* Info pills */}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 ml-4 text-sm text-gray-600">
                  {formatSchedule(course) && (
                    <span title="Schedule">
                      <span className="text-gray-400 mr-1.5">&#x1F4C5;</span>
                      {formatSchedule(course)}
                    </span>
                  )}
                  <span title="Location">
                    <span className="text-gray-400 mr-1.5">&#x1F4CD;</span>
                    {LOCATION_LABELS[course.location?.type] || 'In-Person'}
                  </span>
                  <span title="Students">
                    <span className="text-gray-400 mr-1.5">&#x1F465;</span>
                    {course.enrolledCount || 0} students
                  </span>
                  {formatPrice(course) && (
                    <span title="Price">
                      <span className="text-gray-400 mr-1.5">&#x1F4B0;</span>
                      {formatPrice(course)}
                    </span>
                  )}
                  <span title="Discounts">
                    <span className="text-gray-400 mr-1.5">&#x1F3F7;</span>
                    Discounts:
                    <span className={`ml-1 font-medium ${course.discountsEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                      {course.discountsEnabled ? 'On' : 'Off'}
                    </span>
                  </span>
                </div>
              </div>

              {/* Actions bar — horizontally scrollable on mobile so all actions stay
                  readable text. Each action gets shrink-0 + whitespace-nowrap. */}
              {tab === 'active' && (
                <div className="border-t border-gray-100 bg-gray-50 overflow-x-auto scrollbar-hide">
                  <div className="px-5 py-3 flex items-center gap-4 whitespace-nowrap">
                    {course.status === 'draft' && (
                      <button
                        onClick={() => handleStatusChange(course._id, 'active')}
                        className="shrink-0 text-sm text-green-600 hover:text-green-700 font-medium"
                      >
                        Publish
                      </button>
                    )}
                    {course.status === 'active' && studio?.slug && (
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}/s/${studio.slug}/enroll/${course._id}`;
                          navigator.clipboard.writeText(url);
                          setCopiedId(course._id);
                          setTimeout(() => setCopiedId(null), 2000);
                        }}
                        className="shrink-0 text-sm text-brand-500 hover:text-brand-600 font-medium"
                      >
                        {copiedId === course._id ? 'Copied!' : 'Share Link'}
                      </button>
                    )}
                    <button
                      onClick={() => setEditingCourse(course)}
                      className="shrink-0 text-sm text-gray-600 hover:text-gray-900 font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setManagingMaterials(course)}
                      className="shrink-0 text-sm text-gray-600 hover:text-gray-900 font-medium"
                    >
                      Materials
                    </button>
                    <button
                      onClick={() => setManagingAssessments(course)}
                      className="shrink-0 text-sm text-gray-600 hover:text-gray-900 font-medium"
                    >
                      Assessments
                    </button>
                    <button
                      onClick={() => { setEditingStep(3); setEditingCourse(course); }}
                      className="shrink-0 text-sm text-gray-600 hover:text-gray-900 font-medium"
                    >
                      Discounts
                    </button>
                    {course.googleClassroom?.courseUrl && (
                      <>
                        <a
                          href={course.googleClassroom.courseUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 text-sm text-brand-500 hover:text-brand-600 font-medium"
                        >
                          View in Classroom
                        </a>
                        {course.enrolledCount > 0 && (
                          <button
                            onClick={() => handleInviteStudents(course._id)}
                            disabled={invitingCourseId === course._id}
                            className="shrink-0 text-sm text-gray-600 hover:text-gray-900 font-medium disabled:text-gray-400"
                          >
                            {invitingCourseId === course._id ? 'Inviting...' : 'Invite Students'}
                          </button>
                        )}
                        <button
                          onClick={() => handleSyncMaterials(course._id)}
                          disabled={syncingMaterialsCourseId === course._id}
                          className="shrink-0 text-sm text-gray-600 hover:text-gray-900 font-medium disabled:text-gray-400"
                        >
                          {syncingMaterialsCourseId === course._id ? 'Syncing...' : 'Sync Materials'}
                        </button>
                      </>
                    )}
                    {course.enrolledCount === 0 && (
                      <button
                        onClick={() => setConfirmModal({ open: true, courseId: course._id })}
                        className="shrink-0 text-sm text-red-500 hover:text-red-700 font-medium ml-auto"
                      >
                        Archive
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        open={confirmModal.open}
        title="Archive Course"
        message="This course will be hidden from students and deactivated in Stripe. You can still view it in your records."
        confirmLabel="Archive"
        variant="danger"
        onConfirm={() => handleArchive(confirmModal.courseId)}
        onCancel={() => setConfirmModal({ open: false, courseId: null })}
      />
    </div>
  );
};

export default CoursesPage;
